import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import { geocodingService, type GeocodingResult } from '../../services/GeocodingService';
import { parseCoordinates, type CoordinateParseResult } from '../../services/CoordinateParser';

import { decodeDigipin } from '../../utils/digipinEncoder';

/**
 * Feature search result from attribute search
 */
export interface FeatureSearchResult {
  featureId: string;
  layerId: string;
  layerName: string;
  matchingField: string;
  matchingValue: string;
  geometry: GeoJSON.Geometry;
}

/**
 * DIGIPIN search result
 */
export interface DigipinSearchResult {
  digipin: string;
  rawCode: string;
  coordinates: [number, number];
  latitude: number;
  longitude: number;
  precision: string;
}

/**
 * Search mode determines which type of search is active
 */
export type SearchMode = 'address' | 'coordinate' | 'digipin' | 'feature';

/**
 * Filter options for feature attribute search
 */
export interface SearchFilter {
  layerId?: string;
  fieldName?: string;
}

interface GeocodingState {
  /** Current search query text */
  query: string;
  /** Active search mode */
  searchMode: SearchMode;
  /** Address/place geocoding results */
  geocodingResults: GeocodingResult[];
  /** Feature attribute search results */
  featureSearchResults: FeatureSearchResult[];
  /** Parsed coordinate result */
  coordinateResult: CoordinateParseResult | null;
  /** Parsed DIGIPIN search result */
  digipinResult: DigipinSearchResult | null;
  /** Currently selected result (for marker display) */
  selectedResult: GeocodingResult | null;
  /** Marker coordinates [lon, lat] for display on map */
  markerCoordinates: [number, number] | null;
  /** Loading state */
  isLoading: boolean;
  /** Error message */
  error: string | null;
  /** Whether search panel is open */
  isSearchOpen: boolean;
  /** Filter for feature search */
  searchFilter: SearchFilter;
}

const initialState: GeocodingState = {
  query: '',
  searchMode: 'address',
  geocodingResults: [],
  featureSearchResults: [],
  coordinateResult: null,
  digipinResult: null,
  selectedResult: null,
  markerCoordinates: null,
  isLoading: false,
  error: null,
  isSearchOpen: false,
  searchFilter: {},
};

/**
 * Async thunk: Geocode an address using Nominatim API
 */
export const geocodeAddress = createAsyncThunk(
  'geocoding/geocodeAddress',
  async (address: string, { rejectWithValue }) => {
    try {
      const results = await geocodingService.geocodeAddress(address);
      return results;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Geocoding failed');
    }
  }
);

/**
 * Async thunk: Search features by attribute values across visible layers
 */
export const searchFeatureAttributes = createAsyncThunk(
  'geocoding/searchFeatureAttributes',
  async ({ query, filter }: { query: string; filter?: SearchFilter }, { getState }) => {
    const state = getState() as {
      layers: {
        layers: Array<{
          id: string;
          name: string;
          visible: boolean;
          features?: Array<{
            id: string;
            layerId: string;
            geometry: GeoJSON.Geometry;
            properties: Record<string, any>;
          }>;
        }>;
      };
    };
    const layers = state.layers.layers;
    const results: FeatureSearchResult[] = [];

    if (!query.trim()) return results;

    const searchTerm = query.trim().toLowerCase();

    for (const layer of layers) {
      // Only search visible layers
      if (!layer.visible) continue;
      // Apply layer filter if specified
      if (filter?.layerId && layer.id !== filter.layerId) continue;
      // Skip layers without features
      if (!layer.features || layer.features.length === 0) continue;

      for (const feature of layer.features) {
        if (!feature.properties) continue;

        for (const [fieldName, value] of Object.entries(feature.properties)) {
          // Apply field filter if specified
          if (filter?.fieldName && fieldName !== filter.fieldName) continue;

          const stringValue = String(value ?? '').toLowerCase();
          // Case-insensitive partial matching
          if (stringValue.includes(searchTerm)) {
            results.push({
              featureId: feature.id,
              layerId: layer.id,
              layerName: layer.name,
              matchingField: fieldName,
              matchingValue: String(value ?? ''),
              geometry: feature.geometry,
            });
            break; // Only one result per feature
          }
        }
      }
    }

    return results;
  }
);

const geocodingSlice = createSlice({
  name: 'geocoding',
  initialState,
  reducers: {
    setQuery(state, action: PayloadAction<string>) {
      state.query = action.payload;
    },
    setSearchMode(state, action: PayloadAction<SearchMode>) {
      state.searchMode = action.payload;
      // Clear results when switching modes
      state.geocodingResults = [];
      state.featureSearchResults = [];
      state.coordinateResult = null;
      state.digipinResult = null;
      state.error = null;
    },
    setSearchOpen(state, action: PayloadAction<boolean>) {
      state.isSearchOpen = action.payload;
    },
    setSearchFilter(state, action: PayloadAction<SearchFilter>) {
      state.searchFilter = action.payload;
    },
    parseCoordinateInput(state, action: PayloadAction<string>) {
      const result = parseCoordinates(action.payload);
      state.coordinateResult = result;
      if (result.valid) {
        state.markerCoordinates = result.coordinates;
        state.error = null;
      } else {
        state.error = (result as any).error || 'Invalid coordinate';
        state.markerCoordinates = null;
      }
    },
    parseDigipinInput(state, action: PayloadAction<string>) {
      const decoded = decodeDigipin(action.payload);
      if (decoded) {
        state.digipinResult = {
          digipin: decoded.formatted,
          rawCode: decoded.rawCode,
          coordinates: [decoded.longitude, decoded.latitude],
          latitude: decoded.latitude,
          longitude: decoded.longitude,
          precision: '4m × 4m Grid Cell',
        };
        state.markerCoordinates = [decoded.longitude, decoded.latitude];
        state.error = null;
      } else {
        state.digipinResult = null;
        state.error = 'Invalid DIGIPIN code or location out of bounds (India 4m Grid)';
        state.markerCoordinates = null;
      }
    },
    selectGeocodingResult(state, action: PayloadAction<GeocodingResult>) {
      state.selectedResult = action.payload;
      state.markerCoordinates = action.payload.coordinates;
    },
    setMarkerCoordinates(state, action: PayloadAction<[number, number] | null>) {
      state.markerCoordinates = action.payload;
    },
    clearResults(state) {
      state.geocodingResults = [];
      state.featureSearchResults = [];
      state.coordinateResult = null;
      state.digipinResult = null;
      state.selectedResult = null;
      state.markerCoordinates = null;
      state.error = null;
      state.query = '';
    },
    clearMarker(state) {
      state.markerCoordinates = null;
      state.selectedResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // geocodeAddress
      .addCase(geocodeAddress.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(geocodeAddress.fulfilled, (state, action) => {
        state.isLoading = false;
        state.geocodingResults = action.payload;
        if (action.payload.length === 0) {
          state.error = 'No results found for this search.';
        }
      })
      .addCase(geocodeAddress.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Geocoding request failed';
      })
      // searchFeatureAttributes
      .addCase(searchFeatureAttributes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchFeatureAttributes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.featureSearchResults = action.payload;
        if (action.payload.length === 0) {
          state.error = 'No matching features found.';
        }
      })
      .addCase(searchFeatureAttributes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Feature search failed';
      });
  },
});

export const {
  setQuery,
  setSearchMode,
  setSearchOpen,
  setSearchFilter,
  parseCoordinateInput,
  parseDigipinInput,
  selectGeocodingResult,
  setMarkerCoordinates,
  clearResults,
  clearMarker,
} = geocodingSlice.actions;

export default geocodingSlice.reducer;
